//@ts-nocheck
import { parseISO } from 'date-fns';
import { t } from 'i18next';

export const isValidDate = (date) => {
  if (!date) return false;

  const parsedDate = date instanceof Date ? date : new Date(date);

  return parsedDate instanceof Date && !isNaN(parsedDate.getTime());
};

export const getDateWithoutOffset = (date) => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};

export const parseISODateWithoutOffset = (date) => {
  return date && typeof date === 'string' ? parseISO(date.split('T')[0]) : date;
};

export const toUTCNoonDate = (input) => {
  if (!input) return null;

  const raw =
    input instanceof Date
      ? input.toISOString()
      : typeof input === 'string'
        ? input
        : null;

  if (!raw) return null;

  const [ymd] = raw.split('T');
  if (!ymd || !/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(ymd)) {
    return null;
  }

  const [year, month, day] = ymd.split('-').map((segment) => Number(segment));
  if ([year, month, day].some((value) => Number.isNaN(value))) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

export const getDayOfWeekName = (date) => {
  if (!date) {
    return '';
  }

  return date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
};

// LineItem time fields are stored as timezone-agnostic strings.
// New format: "HH:mm" (24-hour, 5 chars). Legacy values may include ISO strings like
// "1970-01-01T09:30:00.000Z" or "HH:mm:ss". This utility normalizes to "HH:mm"
// without using Date parsing (no timezone conversion).
export const normalizeLineItemTimeToHHmm = (rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') return '';

  const trimmed = rawValue.trim();

  // Preferred format: HH:mm
  if (/^\d{2}:\d{2}$/.test(trimmed)) return trimmed;

  // Accept HH:mm:ss
  const hhmmssMatch = trimmed.match(/^(\d{2}):(\d{2}):\d{2}$/);
  if (hhmmssMatch) return `${hhmmssMatch[1]}:${hhmmssMatch[2]}`;

  // Legacy ISO strings (e.g., 1970-01-01T09:30:00.000Z)
  const isoMatch = trimmed.match(/T(\d{2}):(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;

  return '';
};

export const normalizeLineItemDurationToHHmm = (rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') return '';

  const trimmed = rawValue.trim();

  if (/^\d{2}:\d{2}$/.test(trimmed)) return trimmed;

  const hColonMmMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (hColonMmMatch) {
    const hh = String(Number(hColonMmMatch[1])).padStart(2, '0');
    return `${hh}:${hColonMmMatch[2]}`;
  }

  const hhmmssMatch = trimmed.match(/^([0-9]{1,2}):([0-9]{2}):[0-9]{2}$/);
  if (hhmmssMatch) {
    const hh = String(Number(hhmmssMatch[1])).padStart(2, '0');
    return `${hh}:${hhmmssMatch[2]}`;
  }

  const isoMatch = trimmed.match(/T(\d{2}):(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}:${isoMatch[2]}`;

  return '';
};

/**
 * Converts a time picker value to an ISO string for storage.
 * For SERVICE TIMES (not system timestamps), we store the "wall clock" time
 * in UTC format but treat it timezone-agnostically.
 *
 * Example: User selects 9:30 AM → stores as "1970-01-01T09:30:00.000Z"
 * This ensures 9:30 AM displays as 9:30 AM regardless of user timezone.
 *
 * @param {Date} localDate - The time selected by the user
 * @returns {string} ISO string with time component (date portion is arbitrary)
 */
export const utcTimeISO = (localDate) => {
  if (!localDate) return;

  // Extract the local time components (what the user sees)
  // Use native methods instead of date-fns to ensure we get local time
  const hours = localDate.getHours();
  const minutes = localDate.getMinutes();

  // Store these time components as UTC (treating them as "wall clock" time)
  // Use epoch date (1970-01-01) as arbitrary date component
  const utcDate = new Date(Date.UTC(1970, 0, 1, hours, minutes, 0));

  const iso = utcDate.toISOString();

  return iso;
};

/* removeTimeFromDate
 *
 * Takes an input as a date, parses it only keeping the year, month, and day, then returns a correct UTC date.
 * See XGE-2058.
 * Intended to counteract local times in MUI DatePicker values (see RenderField component for dates)
 * to ensure that dates are stored correctly in the DB, regardless of the user's current timezone.
 */
export const removeTimeFromDate = (input) => {
  if (!input) return null;

  // Create a Date object from the input string
  const date = new Date(input);

  if (isNaN(date.getTime())) {
    return null; // Invalid date
  }

  // Extract the year, month, and date
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  const day = date.getDate();

  // Return a new Date at midnight UTC
  const output = new Date(Date.UTC(year, month, day));
  return output;
};

// Convert the UTC string to a Date with UTC time but local time
export const utcStringToLocalDate = (utcString) => {
  if (!utcString) return null;
  // Parse the UTC string to a Date object
  const utcDate = new Date(utcString);
  if (isNaN(utcDate.getTime())) {
    return null; // Invalid date
  }
  // Create a new Date object with the same time but in local timezone
  const localDate = new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
    utcDate.getUTCHours(),
    utcDate.getUTCMinutes(),
    utcDate.getUTCSeconds()
  );

  return localDate;
};

export const formatDateWithAddedDay = (
  date,
  daysToAdd = 0,
  locale = 'en-US',
  options = { month: 'long', year: 'numeric' }
) => {
  const dateWithDay = new Date(date);
  if (daysToAdd > 0) {
    dateWithDay.setDate(dateWithDay.getDate() + daysToAdd);
  }

  const formatter = new Intl.DateTimeFormat(locale, options);
  const formatDay = (date) => String(date.getDate()).padStart(2, '0');

  return `${formatDay(dateWithDay)} ${formatter.format(dateWithDay)}`;
};

export const formatDateRange = (
  startDate,
  endDate,
  locale = 'en-US',
  options = { month: 'long', year: 'numeric' }
) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (!startDate || !endDate) return '';

  return `${formatDateWithAddedDay(start, 0, locale, options)} - ${formatDateWithAddedDay(end, 0, locale, options)}`;
};

export const formatDateWithAddedDays = (
  startDate,
  daysToAdd,
  locale,
  options
) => {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + daysToAdd);
  return formatDateRange(startDate, endDate, { locale, options });
};

export const formatDateWithLocale = (
  date,
  locale = 'en-US',
  options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }
) => {
  if (!date || Number.isNaN(Date.parse(date))) {
    return date;
  }

  const formatter = new Intl.DateTimeFormat(locale, options);
  return formatter.format(new Date(date));
};

export const formatDate = (dateString, format) => {
  // Ensure a value is provided
  if (!isValidDate(dateString)) return '';

  const date = new Date(dateString);

  const pad = (num, size = 2) => num.toString().padStart(size, '0');

  const formatMap = {
    YYYY: date.getUTCFullYear().toString(),
    MM: pad(date.getUTCMonth() + 1),
    DD: pad(date.getUTCDate()),
    HH: pad(date.getUTCHours()),
    mm: pad(date.getUTCMinutes()),
    ss: pad(date.getUTCSeconds())
  };

  return format.replace(/YYYY|MM|DD|HH|mm|ss/g, (match) => formatMap[match]);
};

export const extractHourAndMinutesUTC = (isoDateString) => {
  if (!isValidDate(isoDateString)) return '';
  const date = new Date(isoDateString);
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  return `${hours} ${hours ? t('common:hours').toUpperCase() : ''} ${t('common:and')} ${minutes} ${minutes ? t('common:minutes').toUpperCase() : ''}`;
};

/**
 * Formats duration from a date string to HH:MM format.
 * Extracts time directly from ISO string to avoid timezone conversion.
 *
 * @param {string} dateString - ISO string containing duration
 * @returns {string} Formatted duration (e.g., "1:30")
 */
export const formatDurationFromDate = (dateString) => {
  if (!dateString) return '';

  // Extract time directly from ISO string to avoid timezone conversion
  let timePart = null;

  if (typeof dateString === 'string') {
    const timeMatch = dateString.match(/T(\d{2}:\d{2}:\d{2})/);
    timePart = timeMatch ? timeMatch[1] : null;
  }

  if (!timePart && isValidDate(dateString)) {
    const match = dateString.toString().match(/\d{2}:\d{2}:\d{2}/);
    timePart = match ? match[0] : null;
  }

  if (!timePart) return '';

  const [hours, minutes] = timePart.split(':');
  const formattedHours = hours;
  const formattedMinutes = minutes > 0 ? `:${minutes}` : '';
  const formattedTime = `${formattedHours}${formattedMinutes}`;
  return formattedTime;
};

/**
 * Formats duration from HH:MM format to "X hours Y minutes" with proper singular/plural forms
 * Follows requirements: lowercase, hide zero values, no "and", proper singular forms
 * @param {string} durationString - Duration in HH:MM format or ISO date string
 * @returns {string} Formatted duration like "2 hours 30 minutes" or "1 hour" or "45 minutes"
 */
export const formatDurationDisplay = (durationString) => {
  if (!durationString) return '';

  let hours = 0;
  let minutes = 0;

  // Handle ISO string format - extract time directly to avoid timezone conversion
  if (typeof durationString === 'string' && durationString.includes('T')) {
    const timeMatch = durationString.match(/T(\d{2}):(\d{2})/);
    if (timeMatch) {
      hours = parseInt(timeMatch[1], 10);
      minutes = parseInt(timeMatch[2], 10);
    }
  }
  // Handle HH:MM format directly
  else if (typeof durationString === 'string' && durationString.includes(':')) {
    const timeMatch = durationString.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      hours = parseInt(timeMatch[1], 10);
      minutes = parseInt(timeMatch[2], 10);
    }
  }
  // Fallback for other formats
  else {
    const match = durationString
      .toString()
      .match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (match) {
      hours = parseInt(match[1], 10);
      minutes = parseInt(match[2], 10);
    }
  }

  const parts = [];

  // Add hours if not zero
  if (hours > 0) {
    const hourLabel =
      hours === 1
        ? t('common:hour').toLowerCase()
        : t('common:hours').toLowerCase();
    parts.push(`${hours} ${hourLabel}`);
  }

  // Add minutes if not zero
  if (minutes > 0) {
    const minuteLabel =
      minutes === 1
        ? t('common:minute').toLowerCase()
        : t('common:minutes').toLowerCase();
    parts.push(`${minutes} ${minuteLabel}`);
  }

  // If both are zero, return empty string
  if (parts.length === 0) {
    return '';
  }

  // Join parts with space (no "and")
  return parts.join(' ');
};

export const formatFullDate = (
  date,
  language = 'en-US',
  timeZone = 'Europe/London'
) => {
  // Convert to the specified timezone
  const options = { timeZone, hour12: true };
  const localDate = new Date(date.toLocaleString(language, options));

  // Extract date parts manually
  const day = localDate.getDate();
  const month = localDate.toLocaleString(language, { month: 'short' });
  const year = localDate.getFullYear();
  const time = formatTime(localDate);

  const tz =
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(localDate)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return `${day} ${month} ${year} ${time} ${tz}`;
};

export const formatDateWithTimezone = (
  dateValue,
  timeZone = 'Europe/London'
) => {
  // Ensure a value is provided
  if (!isValidDate(dateValue)) return '';

  // Convert both date and time to the correct timezone
  const options = { timeZone, hour12: true };
  const localDate = new Date(dateValue.toLocaleString('en-US', options));

  // Extract date parts
  const day = localDate.getDate();
  const month = localDate.toLocaleString('en-US', { month: 'short' });
  const year = localDate.getFullYear();

  return `${day} ${month} ${year} ${formatTimeWithTimezone(dateValue, timeZone)}`;
};

export const formatDateTimeWithTimezone = (
  dateValue,
  timeValue,
  timeZone = 'Europe/London'
) => {
  // Ensure a value is provided
  if (!isValidDate(dateValue))
    return formatTimeWithTimezone(timeValue, timeZone);

  // Convert both date and time to the correct timezone
  const options = { timeZone, hour12: true };
  const localDate = new Date(dateValue.toLocaleString('en-US', options));

  // Extract date parts
  const day = localDate.getDate();
  const month = localDate.toLocaleString('en-US', { month: 'short' });
  const year = localDate.getFullYear();

  return `${day} ${month} ${year} ${formatTimeWithTimezone(timeValue, timeZone)}`;
};

export const formatTimeWithTimezone = (
  timeValue,
  timeZone = 'Europe/London'
) => {
  // Ensure a value is provided
  if (!isValidDate(timeValue)) return '';

  const options = { timeZone, hour12: true };
  const localTime = new Date(timeValue.toLocaleString('en-US', options));
  const time = formatTime(timeValue);

  const tz =
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(localTime)
      .find((part) => part.type === 'timeZoneName')?.value || '';

  return `${time} ${tz}`;
};

export const formatDateTime = (dateValue, timeValue) => {
  const options = { hour12: true };
  let dateSegment = '';
  let timeSegment = '';

  if (isValidDate(dateValue)) {
    const localDate = new Date(dateValue?.toLocaleString('en-US', options));
    const day = localDate.getDate();
    const month = localDate.toLocaleString('en-US', { month: 'short' });
    const year = localDate.getFullYear();
    dateSegment = `${day} ${month} ${year}`;
  }

  if (isValidDate(timeValue)) {
    const date = new Date(timeValue);

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours === 0 ? 12 : hours; // Convierte 0 a 12

    const formattedMinutes = minutes.toString().padStart(2, '0');
    timeSegment = `${hours}:${formattedMinutes} ${ampm}`;
  }

  return `${dateSegment} ${timeSegment}`;
};

/**
 * Formats a time value for display.
 * For SERVICE TIMES, extracts time directly from ISO string without timezone conversion.
 * This ensures times display as "wall clock" times (9:30 AM always shows as 9:30 AM).
 *
 * @param {string|Date} timeValue - ISO string or Date object containing time
 * @returns {string} Formatted time string (e.g., "9:30 AM")
 */
export const formatTime = (timeValue) => {
  if (!timeValue) return '';

  // Extract time portion directly from ISO string to avoid timezone conversion
  let timePart = null;

  if (typeof timeValue === 'string') {
    // Extract HH:MM:SS from ISO string (e.g., "1970-01-01T09:30:00.000Z" -> "09:30:00")
    const timeMatch = timeValue.match(/T(\d{2}:\d{2}:\d{2})/);
    timePart = timeMatch ? timeMatch[1] : null;
  } else if (isValidDate(timeValue)) {
    // Fallback for Date objects - extract from string representation
    const match = timeValue.toString().match(/\d{2}:\d{2}:\d{2}/);
    timePart = match ? match[0] : null;
  }

  if (!timePart) return '';

  const [hours, minutes] = timePart.split(':');
  const hourNum = parseInt(hours, 10);
  const amPm = hourNum >= 12 ? 'PM' : 'AM';
  const formattedHours = hourNum % 12 || 12;
  const formattedMinutes = minutes > 0 ? `:${minutes}` : ':00';
  const formattedTime = `${formattedHours}${formattedMinutes} ${amPm}`;
  return formattedTime;
};

/**
 * formatDateForDisplay
 *
 * Takes an input date, extracts the year/month/date, sets time to 12:00 UTC,
 * and displays it as "dd MMM yyyy" format.
 * This ensures consistent date display regardless of user's timezone.
 *
 * @param {Date|string} input - The input date
 * @returns {string} - Formatted date string (e.g., "25 Jul 2025")
 */
export const formatDateForDisplay = (input) => {
  if (!input) return '';

  const date = new Date(input);

  if (isNaN(date.getTime())) {
    return ''; // Invalid date
  }

  // Extract year, month, day from the input date
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  const day = date.getDate();

  // Create UTC date at noon to avoid timezone issues
  const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));

  // Format as "dd MMM yyyy"
  return utcDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

/**
 * convertInputDate
 *
 * Takes an input date in local timezone, extracts the year/month/date,
 * sets time to 12:00 UTC, and returns it as a UTC datetime.
 * This is for converting user input dates to consistent UTC storage format.
 *
 * @param {Date|string} input - The input date in local timezone
 * @returns {Date} - UTC Date object set to noon UTC
 */
export const convertInputDate = (input) => {
  if (!input) return null;

  const date = new Date(input);

  if (isNaN(date.getTime())) {
    return null; // Invalid date
  }

  // Extract year, month, day from the input date (in user's local timezone)
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  const day = date.getDate();

  // Create UTC date at noon (12:00 UTC) to avoid timezone boundary issues
  const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));

  return utcDate;
};

/**
 * convertInputDateWithAddedDay
 *
 * Takes an input date in local timezone, extracts the year/month/day,
 * adds the given number of days, sets time to 12:00 UTC, and returns it as a UTC datetime.
 * This is for converting user input dates to consistent UTC storage format.
 *
 * @param {Date|string} input - The input date in local timezone
 * @param {number} [daysToAdd=0] - Number of days to add
 * @returns {Date} - UTC Date object set to noon UTC
 */
export const convertInputDateWithAddedDay = (input, daysToAdd = 0) => {
  if (!input) return null;

  const date = new Date(input);

  if (isNaN(date.getTime())) {
    return null; // Invalid date
  }

  // Extract year, month, day from the input date (in user's local timezone)
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based
  const day = date.getDate();

  // Create UTC date at noon (12:00 UTC) to avoid timezone boundary issues
  const utcDate = new Date(Date.UTC(year, month, day + daysToAdd, 12, 0, 0, 0));

  return utcDate;
};

/**
 * formatDateWithAddedDayUTC
 *
 * UTC version of formatDateWithAddedDay that ensures consistent date calculation
 * regardless of user's timezone. Adds days to a date and formats as "dd Month yyyy".
 *
 * @param {Date|string} date - The base date
 * @param {number} daysToAdd - Number of days to add (default: 0)
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string (e.g., "25 July 2025")
 */
export const formatDateWithAddedDayUTC = (
  date,
  daysToAdd = 0,
  locale = 'en-US',
  options = { month: 'long', year: 'numeric' }
) => {
  if (!date) return '';

  const inputDate = new Date(date);
  if (isNaN(inputDate.getTime())) {
    return '';
  }

  // Extract year, month, day and add days in UTC
  const year = inputDate.getFullYear();
  const month = inputDate.getMonth();
  const day = inputDate.getDate();

  // Create UTC date at noon and add days
  const utcDate = new Date(Date.UTC(year, month, day + daysToAdd, 12, 0, 0, 0));

  const formatter = new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: 'UTC'
  });
  const formatDay = (date) => String(date.getUTCDate()).padStart(2, '0');

  return `${formatDay(utcDate)} ${formatter.format(utcDate)}`;
};

/**
 * formatDateWithAddedDaysUTC
 *
 * UTC version of formatDateWithAddedDays that ensures consistent date range calculation
 * regardless of user's timezone. Creates a date range from start date to start date + days.
 *
 * @param {Date|string} startDate - The start date
 * @param {number} daysToAdd - Number of days to add for end date
 * @param {string} locale - Locale for formatting
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date range string
 */
export const formatDateWithAddedDaysUTC = (
  startDate,
  daysToAdd,
  locale,
  options
) => {
  if (!startDate) return '';

  const inputDate = new Date(startDate);
  if (isNaN(inputDate.getTime())) {
    return '';
  }

  // Extract year, month, day from start date
  const year = inputDate.getFullYear();
  const month = inputDate.getMonth();
  const day = inputDate.getDate();

  // Create UTC start and end dates at noon
  const utcStartDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
  const utcEndDate = new Date(
    Date.UTC(year, month, day + daysToAdd, 12, 0, 0, 0)
  );

  return formatDateRange(utcStartDate, utcEndDate, { locale, options });
};

/**
 * Get today's date in ISO format (YYYY-MM-DD)
 * @param {*} date - Optional date parameter, defaults to current date if not provided
 * @returns   {string} - Today's date in ISO format (YYYY-MM-DD)
 *
 * This function creates a new Date object with the current year, month, and day,
 * ensuring that the time is set to midnight UTC. It returns the date in ISO format.
 * This is useful for getting a consistent date representation without time offset issues.
 */
export const getTodayISO = () => {
  let date = new Date();

  // Create a new date object with the same year, month, and day as the input date
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).toISOString();
};

/**
 * Returns the check-in date for a given start date and day number.
 *
 * @param {Date|string} startdate - The start date
 * @param {number} daynumber - The day number
 * @returns {Date|null} - The check-in date, or null if either parameter is invalid
 */
export const getCheckInDate = (startdate, daynumber) => {
  const numericDay = Number(daynumber);

  if (Number.isNaN(numericDay)) {
    return null;
  }

  const normalizeToDateOnly = (value) => {
    if (!value) {
      return null;
    }

    let baseDate = null;

    if (value instanceof Date) {
      baseDate = value;
    } else if (typeof value === 'string') {
      baseDate = parseISODateWithoutOffset(value);
    }

    if (!baseDate || Number.isNaN(baseDate.getTime())) {
      return null;
    }

    return new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate()
    );
  };

  const startDateOnly = normalizeToDateOnly(startdate);

  if (!startDateOnly) {
    return null;
  }

  const offset = Math.max(numericDay - 1, 0);

  return convertInputDateWithAddedDay(startDateOnly, offset);
};

/**
 * Returns the check-out date for a given check-in date and number of nights.
 *
 * @param {Date|string} checkInDate - The check-in date
 * @param {number} numberOfNights - The number of nights (default: 1)
 * @returns {Date|null} - The check-out date, or null if either parameter is invalid
 */
export const getCheckOutDate = (checkInDate, numberOfNights = 1) => {
  if (typeof numberOfNights === 'number' && checkInDate) {
    return convertInputDateWithAddedDay(checkInDate, Number(numberOfNights));
  }
  return null;
};

// Returns date in YYYY-MM-DD format for API consumption
export const formatDateForAPI = (date) => {
  if (!date) return null;

  // Get the local date components (what the user sees on calendar)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

//Returns date in YYYY-MM-DD format
export const parseDateFromString = (dateString) => {
  if (!dateString) return null;

  // Parse YYYY-MM-DD as local date (not UTC)
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

/**
 * Detects whether the user's locale prefers 12-hour or 24-hour time format
 * by testing a sample time with the browser's locale settings.
 *
 * @returns {boolean} - true if 12-hour format, false if 24-hour format
 */
export const detectTimeFormat = () => {
  try {
    // Get user's browser locale
    const locale = navigator.language || navigator.userLanguage || 'en-US';

    // Create a sample time to test format (e.g., 2:30 PM / 14:30)
    const sampleDate = new Date(2024, 0, 1, 14, 30, 0);

    // Format the time using the user's locale
    const formattedTime = sampleDate.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: undefined // Let the locale decide
    });

    // Check if the formatted time contains AM/PM indicators
    const hasAmPm = /AM|PM|am|pm|a\.m\.|p\.m\./i.test(formattedTime);

    return hasAmPm; // true = 12-hour format, false = 24-hour format
  } catch (error) {
    console.warn('Error detecting time format, defaulting to 12-hour:', error);
    return true; // Default to 12-hour format
  }
};

/**
 * Gets the user's preferred time format from browser locale.
 * Returns '12h' or '24h' as a string for easier state management.
 *
 * @returns {string} - '12h' or '24h'
 */
export const getUserTimeFormat = () => {
  const is12Hour = detectTimeFormat();
  return is12Hour ? '12h' : '24h';
};

/**
 * Formats a time value for display using the user's locale-based time format preference.
 * For SERVICE TIMES, extracts time directly from ISO string without timezone conversion.
 * This ensures times display as "wall clock" times and in the user's preferred format.
 *
 * Supports both 12-hour (2:30 PM) and 24-hour (14:30) formats based on user's browser locale.
 *
 * @param {string|Date} timeValue - ISO string or Date object containing time
 * @param {string} [forceFormat] - Optional: '12h' or '24h' to override locale detection
 * @returns {string} Formatted time string (e.g., "9:30 AM" or "09:30")
 */
export const formatTimeLocale = (timeValue, forceFormat = null) => {
  if (!timeValue) return '';

  // Extract time portion directly from ISO string to avoid timezone conversion
  let timePart = null;

  if (typeof timeValue === 'string') {
    // First check if it's a simple time string (e.g., "10:00" or "10:00:00")
    const simpleTimeMatch = timeValue.match(/^(\d{1,2}:\d{2}(?::\d{2})?)$/);
    if (simpleTimeMatch) {
      timePart = simpleTimeMatch[1];
      // Ensure we have seconds (add :00 if missing)
      if (!timePart.includes(':00:') && timePart.split(':').length === 2) {
        timePart = `${timePart}:00`;
      }
    } else {
      // Extract HH:MM:SS from ISO string (e.g., "1970-01-01T09:30:00.000Z" -> "09:30:00")
      const timeMatch = timeValue.match(/T(\d{2}:\d{2}:\d{2})/);
      timePart = timeMatch ? timeMatch[1] : null;
    }
  } else if (isValidDate(timeValue)) {
    // Fallback for Date objects - extract from string representation
    const match = timeValue.toString().match(/\d{2}:\d{2}:\d{2}/);
    timePart = match ? match[0] : null;
  }

  if (!timePart) return '';

  const [hours, minutes] = timePart.split(':');
  const hourNum = parseInt(hours, 10);
  const minuteNum = parseInt(minutes, 10);

  // Determine format: use forced format if provided, otherwise detect from locale
  const use12Hour = forceFormat ? forceFormat === '12h' : detectTimeFormat();

  if (use12Hour) {
    // 12-hour format with AM/PM
    const amPm = hourNum >= 12 ? 'PM' : 'AM';
    const formattedHours = hourNum % 12 || 12;
    const formattedMinutes = minuteNum > 0 ? `:${minutes}` : ':00';
    return `${formattedHours}${formattedMinutes} ${amPm}`;
  } else {
    // 24-hour format
    const formattedHours = hours; // No zero-padding on hours
    const formattedMinutes = minutes.padStart(2, '0');
    return `${formattedHours}:${formattedMinutes}`;
  }
};

/**
 * Formats a time duration string from HH:mm format to a human-readable format like "X hours Y minutes"
 * @param {string} timeString - Time duration in HH:mm format
 * @returns {string} Formatted time duration string like "X hours Y minutes"
 */
export const formatDuration = (timeString) => {
  // Validate null, undefined, or empty string
  if (!timeString || typeof timeString !== 'string') {
    return '';
  }

  // Validate format HH:mm
  const timeParts = timeString.split(':');
  if (timeParts.length !== 2) {
    return '';
  }

  const [hours, minutes] = timeParts.map(Number);

  // Validate numeric values
  if (isNaN(hours) || isNaN(minutes)) {
    return '';
  }

  const hourText = hours === 1 ? 'hour' : 'hours';
  const minuteText = minutes === 1 ? 'minute' : 'minutes';

  return `${hours} ${hourText} ${minutes} ${minuteText}`;
};
